import React from 'react';
import { API } from 'aws-amplify';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import moment from 'moment';
import './held-lab-results.css';

/**
 * The banner telling a practitioner that a lab sent results we did not file.
 *
 * A lab can push results straight onto a client's record. Two things stop a
 * delivery short of the chart, and neither throws the results away:
 *
 *   A DATE OF BIRTH that does not match the client on file. This is the check
 *   that catches a Client ID pasted onto the wrong patient in the lab's own
 *   software, which nothing else in the chain can see: the id is well formed,
 *   the request is correctly signed, and the practitioner is the right one. Only
 *   a person who knows the client can say which it is.
 *
 *   A PLAN LIMIT already reached. Refusing would lose a result the practitioner
 *   paid a lab to run; writing it would give away what the plan sells.
 *
 * Held results are invisible by nature — nobody asked for them and nothing else
 * on screen implies they exist — so this sits on the Clients page where
 * practitioners already land, rather than on the client's own record. A held
 * result for a client they had no reason to open would otherwise wait forever.
 *
 * Renders NOTHING when there is nothing held, which is almost always.
 */

type HeldDelivery = {
  messageId: string;
  clientId: string;
  held: string;
  lab?: string;
  receivedAt?: string;
  /** The date of birth the LAB sent. Nowhere else: see storedDateOfBirth. */
  sentDateOfBirth?: string;
  /**
   * The date of birth on the client record.
   *
   * Both are carried on the held row rather than read off the test, because the
   * test's embedded client is a snapshot of OUR record — so a screen reading a
   * date of birth from it shows the same value twice and asks the practitioner
   * to judge a mismatch without showing them the mismatch. That shipped once.
   */
  storedDateOfBirth?: string;
  bloodwork?: any;
};

type Props = {
  /** Called after a release, so the caller can reload the test list. */
  onReleased?: (clientId: string) => void;
  /**
   * Open the client's own record for editing, when the caller can.
   *
   * There are three things a date of birth mismatch can mean, and only two of
   * them are decisions about these RESULTS. The third is that our client record
   * is the one with the typo, and that is not a decision about the delivery at
   * all — it is a correction to the client, which belongs on the screen built
   * for it, with that screen's validation.
   *
   * Deliberately not a button that writes the lab's date onto the client.
   * `client.ageAsOf(collectionDate)` picks the reference range for every marker,
   * so changing a date of birth re-grades every test that client has ever had —
   * and the date being offered comes from an outside system that may itself be
   * the one that is wrong. That is too much to hang on one click in a modal.
   */
  onOpenClient?: (clientId: string) => void;
};

type State = {
  held: HeldDelivery[];
  reviewing?: HeldDelivery;
  busy: boolean;
  error?: string;
};

const DOB_MISMATCH = 'date_of_birth_mismatch';
/*
 * A delivery that would have filed a test with nothing visible on it: no marker
 * LabSmarts charts, and no row this practitioner's own library claimed. Held
 * rather than written, because nobody asked for the upload and a blank test
 * that eats a plan slot reads as a fault. Produced by the PDF-drop route; see
 * HELD.NOTHING_CHARTED in lambdas/labResults/ingest.js.
 */
const NOTHING_CHARTED = 'nothing_charted';

/**
 * A date as the rest of the app prints one, or a dash when there is none.
 *
 * Read as a CALENDAR DATE, never converted through a timezone. The two values
 * this screen exists to compare arrive in different shapes — the lab sends a
 * bare `1900-01-01` and a client record carries `1977-07-07T06:00:00.000Z` — and
 * putting the second through local formatting moves it: in Pacific that renders
 * as 07/06/1977, a day early. On a screen whose whole job is asking someone to
 * compare two dates of birth, a date that is off by one in half the country is
 * worse than useless.
 *
 * Anything leading with YYYY-MM-DD is taken at its word. Anything else falls
 * through to moment, which covers a value that is genuinely a moment in time.
 */
export function printedDate(value: any): string {
  const text = String(value == null ? '' : value);
  const calendar = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (calendar) {
    const [, year, month, day] = calendar;
    return `${month}/${day}/${year}`;
  }
  const parsed = moment(value);
  return value && parsed.isValid() ? parsed.format('MM/DD/YYYY') : '—';
}

/**
 * What to call a held delivery in one line.
 *
 * Exported for its own test: it is the only part of this a practitioner reads
 * word for word, and it has to stay true when the client is one we cannot name
 * (a delivery for a client since deleted) and when the lab did not say who it
 * was.
 */
export function clientNameOf(delivery: HeldDelivery): string {
  const client = (delivery.bloodwork || {}).client || {};
  return `${client.firstName || ''} ${client.lastName || ''}`.trim();
}

/**
 * The lab's name as a practitioner knows it, from the stored integration key.
 *
 * `virtualClinic` is what the ingest records, because that key is also what the
 * practitioner's consent list holds and what the endpoint checks. It is not a
 * name anyone should be shown.
 */
export function labNameOf(delivery: HeldDelivery): string {
  return delivery.lab === 'virtualClinic'
    ? 'Virtual Clinic'
    : delivery.lab || 'the lab';
}

export function describeHeld(
  delivery: HeldDelivery,
  clientName?: string,
): string {
  const who = clientName || 'a client';
  const lab = labNameOf(delivery);
  const sender = lab === 'the lab' ? 'A lab' : lab;
  if (delivery.held === DOB_MISMATCH) {
    return `${sender} sent test results for ${who} whose date of birth does not match.`;
  }
  /*
   * Spelled out rather than left to the test-limit fallback this used to be.
   * A ternary here meant every reason that was not a date of birth mismatch
   * told the practitioner their account was full, which for this one is simply
   * untrue and sends them to the billing page over a report they could file by
   * adding a marker.
   */
  if (delivery.held === NOTHING_CHARTED) {
    return `${sender} sent results for ${who} that LabSmarts does not chart. Add the markers you want to My Markers, then add these results.`;
  }
  return `${sender} sent test results for ${who}, held because the account is at its test limit.`;
}

/** "1 lab result is waiting" / "3 lab results are waiting". */
export function describeCount(count: number): string {
  return count === 1
    ? '1 lab result is waiting for your review'
    : `${count} lab results are waiting for your review`;
}

export class HeldLabResults extends React.Component<Props, State> {
  state: State = { held: [], busy: false };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    try {
      const held = await API.get('restAPI', '/bloodwork/quarantine', {});
      this.setState({ held: Array.isArray(held) ? held : [] });
    } catch (error) {
      // Silent on purpose. Nothing is held for almost every practitioner almost
      // always, so a failure to load an empty list must not put an error on a
      // page they came to for something else.
      console.error('Could not load held lab results:', error);
    }
  };

  act = async (path: string, delivery: HeldDelivery) => {
    this.setState({ busy: true, error: undefined });
    try {
      await API.post('restAPI', `/bloodwork/quarantine/${path}`, {
        body: { messageId: delivery.messageId },
      });
      this.setState(prev => ({
        held: prev.held.filter(h => h.messageId !== delivery.messageId),
        reviewing: undefined,
        busy: false,
      }));
      if (path === 'release' && this.props.onReleased) {
        this.props.onReleased(delivery.clientId);
      }
    } catch (error) {
      // The API layer wraps the body; the server's own sentence is the useful
      // one (a release refused for the test limit explains what to do about it).
      const detail: any = error;
      const message =
        (detail && detail.response && detail.response.data && detail.response.data.message) ||
        'That did not go through. Please try again.';
      this.setState({ busy: false, error: message });
    }
  };

  render() {
    const { held, reviewing, busy, error } = this.state;
    if (!held.length) {
      return null;
    }

    const nameFor = (delivery: HeldDelivery) => clientNameOf(delivery);

    return (
      <>
        <Alert variant="warning" className="mb-3">
          <Alert.Heading style={{ fontSize: '1.05rem' }}>
            {describeCount(held.length)}
          </Alert.Heading>
          {held.map(delivery => (
            <div
              key={delivery.messageId}
              className="d-flex justify-content-between align-items-center mt-2"
            >
              <span>{describeHeld(delivery, nameFor(delivery))}</span>
              <Button
                variant="outline-dark"
                size="sm"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => this.setState({ reviewing: delivery, error: undefined })}
              >
                Review
              </Button>
            </div>
          ))}
        </Alert>

        {/* Wide enough that the sentence naming the lab, the client and the
            problem sits on one line. At the default width it wrapped, which
            broke the one thing the reader needs to take in at a glance. */}
        <Modal
          show={!!reviewing}
          onHide={() => this.setState({ reviewing: undefined })}
          size="lg"
          dialogClassName="held-lab-results-dialog"
          centered
        >
          {reviewing && (
            <>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.15rem' }}>
                  Review a Held Lab Result
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {/* Sized explicitly to match the guidance below it. Without
                    this, `.modal-body p { font-size: 12px }` in index.css drops
                    it to 12px app-wide, which left the one sentence saying what
                    happened reading smaller than the notes explaining it. */}
                <p className="mb-3" style={{ fontSize: '0.9rem' }}>
                  {describeHeld(reviewing, nameFor(reviewing))}
                </p>

                {/* 0.9rem, the same as everything else in this dialog.
                    Explicit because `body { font-size: 12px }` in index.css is
                    what these inherit otherwise, which left them smaller than
                    the sentence above — and `rem` is relative to the ROOT 16px,
                    not to that 12px, so 1rem overshot the other way. */}
                <div className="mb-3" style={{ fontSize: '0.9rem' }}>
                  <div>
                    <strong>Collection Date:</strong>{' '}
                    {printedDate(reviewing.bloodwork && reviewing.bloodwork.collectionDate)}
                  </div>
                  <div>
                    <strong>Received from {labNameOf(reviewing)}:</strong>{' '}
                    {printedDate(reviewing.receivedAt)}
                  </div>
                </div>

                {reviewing.held === DOB_MISMATCH && (
                  <Alert variant="light" className="border">
                    {/* The two values the whole dialog exists to compare, so
                        they match the rest of it rather than inheriting
                        `body { font-size: 12px }` from index.css and landing
                        smaller than the notes that explain them. */}
                    <div style={{ fontSize: '0.9rem' }}>
                      <strong>Date of birth {labNameOf(reviewing)} sent:</strong>{' '}
                      {printedDate(reviewing.sentDateOfBirth)}
                    </div>
                    <div style={{ fontSize: '0.9rem' }}>
                      <strong>Date of birth in LabSmarts:</strong>{' '}
                      {printedDate(reviewing.storedDateOfBirth)}
                    </div>
                    <ul
                      className="text-muted mt-2 mb-0 pl-4"
                      style={{ fontSize: '0.9rem' }}
                    >
                      <li>
                        Add these test results only if you are sure they belong
                        to this client.
                      </li>
                      <li>
                        A mismatch usually means the LabSmarts Client ID was
                        entered on the wrong patient in the lab's software, and
                        the results belong to somebody else.
                      </li>
                      <li>
                        If the date of birth in LabSmarts is the one that is
                        wrong, open the client's info using the link below and
                        correct it there before adding.
                      </li>
                    </ul>
                  </Alert>
                )}

                {reviewing.held === NOTHING_CHARTED && (
                  <Alert variant="light" className="border">
                    <ul
                      className="text-muted mb-0 pl-4"
                      style={{ fontSize: '0.9rem' }}
                    >
                      <li>
                        Every result on this report is an analyte LabSmarts has
                        no field for, and none of them matched a marker in your
                        My Markers library, so the test would show nothing.
                      </li>
                      <li>
                        The results are kept. Add the ones you want to My
                        Markers and then add these results, and they will fill
                        in.
                      </li>
                    </ul>
                  </Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}
              </Modal.Body>
              <Modal.Footer className="justify-content-between">
                <Button
                  variant="outline-danger"
                  disabled={busy}
                  onClick={() => this.act('discard', reviewing)}
                >
                  Discard
                </Button>
                {reviewing.held === DOB_MISMATCH && this.props.onOpenClient && (
                  <Button
                    variant="link"
                    className="px-0"
                    /* Underlined because it sits between two real buttons and
                       Bootstrap's .btn-link only underlines on hover, so at rest
                       it reads as disabled text rather than something to click.
                       The bullet above sends the reader looking for a link. */
                    style={{ textDecoration: 'underline' }}
                    disabled={busy}
                    onClick={() => {
                      const clientId = reviewing.clientId;
                      this.setState({ reviewing: undefined });
                      this.props.onOpenClient!(clientId);
                    }}
                  >
                    Open {nameFor(reviewing) || 'this client'}'s client info
                  </Button>
                )}
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() => this.act('release', reviewing)}
                >
                  {busy && <Spinner animation="border" size="sm" className="mr-2" />}
                  Add Test Results for {nameFor(reviewing) || 'this client'}
                </Button>
              </Modal.Footer>
            </>
          )}
        </Modal>
      </>
    );
  }
}
